import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import type { TokenWithBalance } from '@/domain/common/types'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import type { YieldOpportunity } from '@/features/unified-earn/types'
import type { EVaultData } from '@/features/vaults/logic/useEVaults'

import type { UsePortfolioEvmBalancesResult, UseUnifiedPortfolioResult } from './types'

const HOLDINGS_DUST_THRESHOLD = 0.01

export interface WalletBreakdownDisplay {
  nativeUsd: number
  wrappedUsd: number
}

export interface HoldingsData {
  positionUsd?: number
  walletUsd?: number
  subAccountDeposits?: Array<{ subAccountId: number; depositUsd: number }>
  walletBreakdown?: WalletBreakdownDisplay
}

interface WalletHoldingsData {
  walletUsd?: number
  walletBreakdown?: WalletBreakdownDisplay
}

type PortfolioWalletSource =
  | Pick<UseUnifiedPortfolioResult, 'balances'>
  | Pick<UsePortfolioEvmBalancesResult, 'balances'>

function removeDust(value: number | undefined): number | undefined {
  if (value === undefined || value < HOLDINGS_DUST_THRESHOLD) return undefined
  return value
}

function tokenAddress(opportunity: YieldOpportunity): string | undefined {
  return opportunity.token?.address?.toLowerCase() ?? opportunity.tokenInfo?.address?.toLowerCase()
}

function findWalletBalance(
  opportunity: YieldOpportunity,
  walletBalances: TokenWithBalance[],
): TokenWithBalance | undefined {
  const address = tokenAddress(opportunity)
  const symbol = opportunity.assetSymbol || opportunity.symbol

  return walletBalances.find((walletToken) => {
    if (address) {
      return walletToken.token.address.toLowerCase() === address
    }

    return walletToken.token.symbol.toString().toLowerCase() === symbol.toLowerCase()
  })
}

function getEvmWalletBalances(source: PortfolioWalletSource): TokenWithBalance[] {
  return Array.isArray(source.balances) ? source.balances : source.balances.evmWallet
}

function getWalletTokenBySymbol(symbol: string, walletBalances: TokenWithBalance[]): TokenWithBalance | undefined {
  return walletBalances.find(
    (walletToken) => walletToken.token.symbol.toString().toLowerCase() === symbol.toLowerCase(),
  )
}

function getWalletTokenUsd(walletBalance: TokenWithBalance | undefined): number {
  if (!walletBalance || walletBalance.balance.lte(0)) return 0
  return walletBalance.token.toUSD(walletBalance.balance).toNumber()
}

function getOpportunityWalletUsd(opportunity: YieldOpportunity): number {
  if (!opportunity.walletBalance || !opportunity.token || opportunity.walletBalance.lte(0)) return 0
  return opportunity.token.toUSD(opportunity.walletBalance).toNumber()
}

function getOpportunityWalletBreakdown(opportunity: YieldOpportunity): WalletBreakdownDisplay | undefined {
  const { walletBreakdown } = opportunity
  if (!walletBreakdown) return undefined

  const { nativeUsd, wrappedUsd } = walletBreakdown
  if (nativeUsd <= HOLDINGS_DUST_THRESHOLD && wrappedUsd <= HOLDINGS_DUST_THRESHOLD) return undefined

  return { nativeUsd, wrappedUsd }
}

function getWalletHoldingsData(opportunity: YieldOpportunity, walletBalances: TokenWithBalance[]): WalletHoldingsData {
  const { nativeAssetSymbol, wrappedNativeAssetSymbol } = getNativeAssetInfo(opportunity.chainId)
  const symbol = opportunity.assetSymbol || opportunity.symbol
  const isNativeAsset =
    symbol.toLowerCase() === nativeAssetSymbol.toLowerCase() ||
    symbol.toLowerCase() === wrappedNativeAssetSymbol.toLowerCase()

  if (isNativeAsset) {
    const nativeUsd = getWalletTokenUsd(getWalletTokenBySymbol(nativeAssetSymbol, walletBalances))
    const wrappedUsd = getWalletTokenUsd(getWalletTokenBySymbol(wrappedNativeAssetSymbol, walletBalances))
    const portfolioWalletUsd = nativeUsd + wrappedUsd
    const fallbackBreakdown = getOpportunityWalletBreakdown(opportunity)
    const fallbackWalletUsd = fallbackBreakdown
      ? fallbackBreakdown.nativeUsd + fallbackBreakdown.wrappedUsd
      : getOpportunityWalletUsd(opportunity)
    const walletUsd = removeDust(portfolioWalletUsd > 0 ? portfolioWalletUsd : fallbackWalletUsd)

    return {
      walletUsd,
      walletBreakdown:
        walletUsd !== undefined ? (portfolioWalletUsd > 0 ? { nativeUsd, wrappedUsd } : fallbackBreakdown) : undefined,
    }
  }

  const walletBalance = findWalletBalance(opportunity, walletBalances)
  const walletUsd = getWalletTokenUsd(walletBalance) || getOpportunityWalletUsd(opportunity)

  return {
    walletUsd: removeDust(walletUsd),
  }
}

function getPositionUsd(opportunity: YieldOpportunity): number | undefined {
  if (opportunity.userDepositUsd?.gt(0)) {
    return opportunity.userDepositUsd.toNumber()
  }

  if (opportunity.userDeposit && opportunity.token && opportunity.userDeposit.gt(0)) {
    return opportunity.token.toUSD(opportunity.userDeposit).toNumber()
  }
}

export function getPortfolioHoldingsData(opportunity: YieldOpportunity, source: PortfolioWalletSource): HoldingsData {
  const walletBalances = getEvmWalletBalances(source)
  const subAccountDeposits =
    opportunity.sourceData.type === 'elend' ? opportunity.sourceData.subAccountDeposits : undefined

  return {
    positionUsd: removeDust(getPositionUsd(opportunity)),
    ...getWalletHoldingsData(opportunity, walletBalances),
    subAccountDeposits,
  }
}

export function getPortfolioHoldingsSortValue(opportunity: YieldOpportunity, source: PortfolioWalletSource): number {
  const data = getPortfolioHoldingsData(opportunity, source)
  return NormalizedUnitNumber(data.positionUsd ?? 0)
    .plus(data.walletUsd ?? 0)
    .toNumber()
}

export function getEVaultHoldingsData(vault: EVaultData): HoldingsData {
  const userBalance = vault.userBalance ?? NormalizedUnitNumber(0)
  const totalAssets = vault.totalAssets
  const totalSupply = vault.totalSupply
  const tvlUsd = vault.tvlUsd ?? NormalizedUnitNumber(0)

  let positionUsd = removeDust(vault.userBalanceUsd?.toNumber())
  if (positionUsd === undefined && userBalance.gt(0) && totalAssets.gt(0) && totalSupply.gt(0) && tvlUsd.gt(0)) {
    const depositedAssets = userBalance.multipliedBy(totalAssets).dividedBy(totalSupply)
    const assetPriceUsd = tvlUsd.dividedBy(totalAssets)
    positionUsd = removeDust(depositedAssets.multipliedBy(assetPriceUsd).toNumber())
  }

  let walletUsd: number | undefined
  const walletBalance = vault.walletBalance ?? NormalizedUnitNumber(0)
  if (vault.token && vault.walletBalance && walletBalance.gt(0)) {
    walletUsd = vault.token.toUSD(vault.walletBalance).toNumber()
  } else if (walletBalance.gt(0) && totalAssets.gt(0) && tvlUsd.gt(0)) {
    const assetPriceUsd = tvlUsd.dividedBy(totalAssets)
    walletUsd = walletBalance.multipliedBy(assetPriceUsd).toNumber()
  }
  walletUsd = removeDust(walletUsd)

  let walletBreakdown: WalletBreakdownDisplay | undefined
  if (vault.walletBreakdown && walletUsd !== undefined && walletUsd > 0) {
    const { nativeUsd, wrappedUsd } = vault.walletBreakdown
    if (nativeUsd > HOLDINGS_DUST_THRESHOLD || wrappedUsd > HOLDINGS_DUST_THRESHOLD) {
      walletBreakdown = { nativeUsd, wrappedUsd }
      walletUsd = nativeUsd + wrappedUsd
    }
  }

  return { positionUsd, walletUsd, walletBreakdown }
}

export function getEVaultHoldingsSortValue(vault: EVaultData): number {
  const { positionUsd, walletUsd } = getEVaultHoldingsData(vault)
  return NormalizedUnitNumber(positionUsd ?? 0)
    .plus(walletUsd ?? 0)
    .toNumber()
}
